import React from 'react';

import { ChatMessage } from './types';

export const USER_MESSAGE_SCROLL_MARGIN_PX = 8;

// Pins the latest user message to the top of the list while its answer comes in. That
// needs enough scrollable space below the message, so the list reserves the missing
// height as bottom padding.
const useReservedResponseSpace = ({
  messages,
  loading,
  activeSessionId,
}: {
  messages: ChatMessage[];
  loading: boolean;
  activeSessionId: string | null;
}) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const latestUserMessageRef = React.useRef<HTMLDivElement>(null);
  const [reservedSpace, setReservedSpace] = React.useState(0);
  const previousSessionId = React.useRef(activeSessionId);

  const latestMessage = messages[messages.length - 1];
  const latestUserMessageIndex = messages.findLastIndex(({ role }) => role === 'user');
  const latestUserMessageContent = messages[latestUserMessageIndex]?.content;
  const reserveSpaceForResponse = loading
    || (latestMessage?.role === 'assistant' && Boolean(latestMessage.justReceived));

  // Space needed below the latest user message's content so that message can be scrolled
  // to the top. It shrinks as the answer below it grows, and is 0 once the answer fills
  // the viewport.
  const computeReservedSpace = React.useCallback((): number => {
    const container = scrollContainerRef.current;
    const userMessage = latestUserMessageRef.current;
    const lastContent = container?.lastElementChild;
    if (!container || !userMessage || !lastContent) return 0;
    const contentBelowUserTop = lastContent.getBoundingClientRect().bottom
      - userMessage.getBoundingClientRect().top;
    return Math.max(0, Math.ceil(
      container.clientHeight - USER_MESSAGE_SCROLL_MARGIN_PX - contentBelowUserTop,
    ));
  }, []);

  React.useLayoutEffect(() => {
    if (previousSessionId.current === activeSessionId) return;
    previousSessionId.current = activeSessionId;
    setReservedSpace(0);
  }, [activeSessionId]);

  const updateReservedSpace = React.useCallback(() => {
    const next = computeReservedSpace();
    setReservedSpace((current) => (Math.abs(current - next) <= 1 ? current : next));
  }, [computeReservedSpace]);

  React.useLayoutEffect(() => {
    if (!reserveSpaceForResponse) {
      setReservedSpace((current) => (current === 0 ? current : 0));
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      updateReservedSpace();
      latestUserMessageRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [latestUserMessageIndex, latestUserMessageContent,
    reserveSpaceForResponse, updateReservedSpace]);

  // Keep the reserved space in step with the content as the answer is typed. Content
  // growth and reserved-space shrink cancel out, so the scroll position does not jump.
  const trackReservedSpace = reserveSpaceForResponse || reservedSpace > 0;
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!trackReservedSpace || !container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    let frame = 0;
    const observer = new ResizeObserver(() => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(updateReservedSpace);
    });
    Array.from(container.children).forEach((child) => observer.observe(child));
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [trackReservedSpace, messages, loading, updateReservedSpace]);

  React.useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [activeSessionId]);

  return { scrollContainerRef, latestUserMessageRef, reservedSpace };
};

export default useReservedResponseSpace;
